'use server';

import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryBentoBoxById, queryContactFormById } from './queries';
import { BentoBox as BentoBoxType } from 'types/generated';
import { Log } from '@/logging/datadog';

const apiUrl = contentfulAPIURL(
  process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
  process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
);
const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

async function fetchBentoBoxById(bentoBoxId: string) {
  const res = queryBentoBoxById(apolloClient, bentoBoxId);

  const json = await res;

  return json.data?.bentoBox;
}

export async function fetchContactFormById(contactFormId: string) {
  const res = queryContactFormById(apolloClient, contactFormId);

  const json = await res;

  return json.data?.contactForm;
}

export const fetchBentoBox = async (bentoBoxId: string) => {
  if (!bentoBoxId) return;

  try {
    const box = (await fetchBentoBoxById(bentoBoxId)) as BentoBoxType;

    if (!box) {
      throw new Error(`BentoBox with ID ${bentoBoxId} not found`);
    } else {
      return {
        ...box,
        contentfulMetadata: box.contentfulMetadata || { tags: [] }, // Ensure contentfulMetadata is set
      };
    }
  } catch (error) {
    Log.error('Error fetching bentoBox:', error);
    return null;
  }
};

async function fetchBentoBoxesByIds(bentoBoxIds: string[]) {
  const allBoxes = await Promise.all(
    bentoBoxIds.map(async (id: string) => {
      const res = queryBentoBoxById(apolloClient, id);

      const json = await res;
      return json.data?.bentoBox;
    })
  );

  return allBoxes.filter((box) => box !== null);
}

export const fetchBentoBoxes = async (bentoBoxIds: string[]) => {
  if (!bentoBoxIds.length) return;

  try {
    const fetchedBoxes = await fetchBentoBoxesByIds(bentoBoxIds);

    return fetchedBoxes.filter((box): box is BentoBoxType => box !== undefined);
  } catch (error) {
    Log.error('Error fetching bentoBoxes:', error);
    return [];
  }
};
